import { motion } from 'framer-motion';
import {
  Target,
  Award,
  Shield,
  ArrowRight,
  Lightbulb,
  TrendingUp,
  DollarSign,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function CompetitiveStrategyPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const genericStrategies = [
    {
      icon: DollarSign,
      title: 'Cost Leadership',
      subtitle: 'Win by Being the Lowest-Cost Provider',
      description:
        'Achieve the lowest cost structure in your industry, enabling you to underprice competitors while maintaining acceptable margins. Volume and efficiency are key.',
      howToWin: [
        'Economies of scale (high volume reduces unit costs)',
        'Process efficiency and automation',
        'Supply chain optimization',
        'Tight cost control across all functions',
        'Standardized products/services',
      ],
      examples: [
        'Walmart (retail)',
        'Southwest Airlines (aviation)',
        'IKEA (furniture)',
        'Costco (wholesale)',
      ],
      warnings: [
        "Don't sacrifice quality below acceptable threshold",
        'Requires sustained volume to maintain cost position',
        'Vulnerable to competitors with deeper pockets',
        'Hard to differentiate beyond price',
      ],
      when: 'Best for mature markets with price-sensitive customers, commoditized products, and where scale economies are achievable',
    },
    {
      icon: Award,
      title: 'Differentiation',
      subtitle: 'Win by Offering Unique Value',
      description:
        'Create products or services perceived as unique and superior, commanding premium prices. Innovation, quality, service, or brand drive differentiation.',
      howToWin: [
        'Superior product quality or features',
        'Exceptional customer service',
        'Strong brand and reputation',
        'Innovation and R&D leadership',
        'Unique design or user experience',
      ],
      examples: [
        'Apple (technology)',
        'Tesla (automotive)',
        'Patagonia (outdoor gear)',
        'Ritz-Carlton (hospitality)',
      ],
      warnings: [
        'Premium pricing requires customers willing and able to pay',
        'Must continuously innovate to maintain differentiation',
        'Risk of competitor imitation',
        "Can't ignore cost completely",
      ],
      when: 'Best when customers value and can afford premium, product/service allows meaningful differentiation, and you have innovation/brand capabilities',
    },
    {
      icon: Shield,
      title: 'Focus (Niche)',
      subtitle: 'Win by Serving a Specific Segment Excellently',
      description:
        'Concentrate on a narrow market segment and tailor everything to their specific needs. Deep specialization creates defensibility.',
      howToWin: [
        'Deep understanding of niche customer needs',
        'Customized products/services for segment',
        'Strong relationships within niche',
        'Reputation as the specialist',
        'Can apply cost focus OR differentiation focus',
      ],
      examples: [
        'Ferrari (luxury supercars)',
        'Whole Foods (organic/natural foods)',
        'Rolls-Royce (jet engines)',
        'Paychex (SMB payroll)',
      ],
      warnings: [
        'Niche must be large enough to sustain business',
        'Risk if niche declines or disappears',
        'Limits growth potential',
        'May face pressure to expand beyond niche',
      ],
      when: 'Best when niche has distinct needs, you lack resources to compete broadly, or niche is underserved and defensible',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Strategy Formulation', path: '/strategy-formulation' },
              { label: 'Competitive Strategy' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Lightbulb className="w-4 h-4 mr-2" />
            Strategy Formulation Tool 1 of 3
          </span>
        }
        title="Choose how your business will compete"
        subtitle="Make a clear choice about where you will play and how you will win. Connect your customer promise, capabilities, and economics into a competitive strategy."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Choose your strategy
        </Link>
      </PageHero>

      {/* Porter's Generic Strategies */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Compare the paths to competitive advantage</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cost leadership, differentiation, and focus each demand different choices. Explore the
              trade-offs before deciding which approach your business can sustain.
            </p>
          </motion.div>
          <div className="space-y-8 max-w-6xl mx-auto">
            {genericStrategies.map((strategy, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <strategy.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{strategy.title}</h3>
                    <p className="text-sm text-blue-700 font-medium">{strategy.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{strategy.description}</p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm font-semibold text-gray-900 mb-2">How to Win:</p>
                    <ul className="space-y-1">
                      {strategy.howToWin.map((h, j) => (
                        <li key={j} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle className="w-3 h-3 text-green-600 mr-1.5 mt-0.5 flex-shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Examples:</p>
                    <div className="flex flex-wrap gap-2">
                      {strategy.examples.map((ex, j) => (
                        <span
                          key={j}
                          className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium"
                        >
                          {ex}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500 mb-3">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Warnings:</p>
                  <ul className="space-y-1">
                    {strategy.warnings.map((w, j) => (
                      <li key={j} className="text-xs text-gray-700 flex items-start">
                        <AlertCircle className="w-3 h-3 text-red-600 mr-1.5 mt-0.5 flex-shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-purple-700">Best When:</span>{' '}
                    {strategy.when}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stuck in the Middle */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-red-50 text-gray-900 p-10 rounded-2xl border border-red-200"
            >
              <div className="flex items-start space-x-4">
                <AlertCircle className="w-12 h-12 text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-3xl font-bold mb-4">The Danger of "Stuck in the Middle"</h2>
                  <p className="text-gray-700 mb-4">
                    Porter's biggest warning: Companies that fail to choose a clear strategy—trying
                    to be both low-cost AND differentiated— end up "stuck in the middle." They're
                    not cheap enough for price-sensitive buyers and not unique enough for
                    value-seekers.
                  </p>
                  <div className="bg-white p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Classic Example:</span> Sears—not as cheap as
                      Walmart, not as differentiated as Nordstrom or specialty retailers. Stuck in
                      middle, slowly died.
                    </p>
                  </div>
                  <p className="text-gray-700 font-semibold">
                    The Fix: Choose one primary strategy. You can't be all things to all people.
                    Commit fully to cost leadership OR differentiation OR focus.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Choose Your Competitive Strategy</h2>
              <p className="text-xl text-gray-600">
                Follow this decision framework to select the right approach
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Assess Your Core Competencies',
                  description:
                    'What are you uniquely good at? If operational efficiency and scale—cost leadership. If innovation and brand—differentiation. If deep niche expertise—focus.',
                  tip: 'Your strategy must leverage actual competencies, not aspirational ones. Be honest about where you truly excel.',
                },
                {
                  step: '2',
                  title: 'Understand Customer Buying Criteria',
                  description:
                    "What drives purchase decisions in your market? If it's primarily price—cost matters. If it's quality/features—differentiation. If it's specialized fit—focus.",
                  tip: 'Interview 20+ customers. Ask: "Why did you choose us? What would make you switch?" Their answers reveal what strategy can work.',
                },
                {
                  step: '3',
                  title: 'Analyze Competitor Positioning',
                  description:
                    'Where are competitors playing? If incumbents own cost leadership (e.g., Walmart), competing there is suicidal without massive capital. Look for open positions.',
                  tip: "Map competitors on cost vs. differentiation grid. Look for gaps where no strong player exists—that's your opening.",
                },
                {
                  step: '4',
                  title: 'Evaluate Resource Requirements',
                  description:
                    'Cost leadership requires scale and capital. Differentiation requires R&D and marketing. Focus requires niche expertise. Do you have—or can you acquire—necessary resources?',
                  tip: 'Cost leadership is capital-intensive upfront. Differentiation is ongoing investment. Choose what you can sustain long-term.',
                },
                {
                  step: '5',
                  title: 'Test for Consistency',
                  description:
                    'Once chosen, strategy must inform all decisions. Product design, pricing, hiring, marketing—everything must align. Mixed signals confuse customers and dilute advantage.',
                  tip: 'Ask: "Does this decision support our cost leadership / differentiation / focus strategy?" If you can\'t answer clearly, strategy isn\'t defined enough.',
                },
                {
                  step: '6',
                  title: 'Commit and Communicate',
                  description:
                    'Half-hearted commitment fails. Announce your strategy clearly internally and externally. Train employees on what it means for their decisions. Measure adherence.',
                  tip: 'Strategy should be one sentence everyone can recite: "We compete on [lowest cost / unique quality / specialized focus] for [broad market / specific segment]."',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Competitive Strategy Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these traps that undermine competitive position
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Trying to Be Everything to Everyone',
                  description:
                    "Attempting both low cost AND premium quality AND niche focus. Result: you're bad at all three. Customers can't understand what you stand for.",
                  fix: 'Pick one primary strategy. You can have secondary attributes (cost leader with decent quality) but one must dominate your positioning.',
                },
                {
                  mistake: 'Changing Strategy Too Often',
                  description:
                    'Switching from differentiation to cost leadership and back. Each strategy requires different capabilities, structure, and culture. Constant changes prevent building necessary depth.',
                  fix: 'Commit to strategy for 3-5 years minimum. Only change for fundamental market shifts, not quarterly results or competitor moves.',
                },
                {
                  mistake: 'Confusing Strategy with Tactics',
                  description:
                    '"Our strategy is social media marketing" isn\'t a competitive strategy—it\'s a tactic. Competitive strategy is your fundamental approach to winning, not your tools.',
                  fix: 'Strategy = how you compete (cost/differentiation/focus). Tactics = how you implement (pricing, channels, marketing). Keep them distinct.',
                },
                {
                  mistake: 'Not Aligning Organization to Strategy',
                  description:
                    'Choosing differentiation but incentivizing cost reduction. Or cost leadership but hiring premium-priced talent. These choices need to support each other for the strategy to hold together.',
                  fix: 'Audit HR, operations, finance, and product against strategy. If systems contradict strategy, change the systems.',
                },
                {
                  mistake: 'Ignoring Competitive Dynamics',
                  description:
                    'Choosing cost leadership when 3 larger players already own that position and can outspend you. Or differentiation in a purely commoditized market.',
                  fix: "Study competitive landscape. Choose strategy that's feasible given competitor strengths and market conditions. Sometimes best strategy = avoid the fight.",
                },
                {
                  mistake: 'No Sustainable Competitive Advantage',
                  description:
                    'Choosing strategy without the competencies or resources to sustain it. Claiming differentiation without innovation capability. Claiming cost leadership without scale.',
                  fix: "Your strategy must build on actual core competencies. If you don't have required competencies, either build them or choose different strategy.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Make the choices behind a coherent strategy
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Choose your customers</h3>
                <p className="text-gray-600">
                  Define the market and customer needs your company will prioritize.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Choose your advantage</h3>
                <p className="text-gray-600">
                  Decide whether your business will compete through cost, difference, or a focused
                  approach.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Align the investment</h3>
                <p className="text-gray-600">
                  Direct your capabilities and initiatives toward the choices that support that
                  advantage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/competitive-positioning"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Competitive Positioning</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Map your position relative to competitors
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/core-competencies"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Award className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Core Competencies</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Identify capabilities that enable your strategy
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/growth-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Growth Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Determine how to scale your competitive advantage
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/strategy-formulation"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Back to Strategy Formulation
              </Link>
              <Link
                to="/strategy-formulation/growth-strategy"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Growth Strategy <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Choose your strategy in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Direct your capabilities and initiatives toward the choices that support that advantage.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Choose your strategy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
